import { t } from "@lingui/macro";
import { captureEvent } from "@sentry/react";
import {
  GetProjectBudgetItemsDocument,
  type GetProjectBudgetItemsQuery,
  type GetProjectBudgetItemsQueryVariables,
  type GlobalCreditAccountFragment,
  TransferGlobalCreditDocument,
  type TransferGlobalCreditMutation,
  type TransferGlobalCreditMutationVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import type {
  TOnCompletePayload,
  TOnReopenPayload,
} from "@src/components/widgets/Modals/ModalCompleteExpense/ModalCompleteExpense";
import type { ExpenseItem } from "@src/components/widgets/Modals/ModalCompleteExpense/models";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import { toApiDateTime } from "@src/utils/dates";
import { currency, denominate, nominate } from "@src/utils/formatters";
import { gt, lte, required } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import type { NumberModel } from "@src/utils/models";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

export type GlobalCreditAccountTransferSourceIds =
  | {
      fromId: string;
      toId?: never;
    }
  | {
      fromId?: never;
      toId: string;
    };

export class TransferGlobalCreditModalStore {
  getCreditModalDisclosure = new DisclosureState({
    onClose: () => {
      this.creditForm.reset();
    },
  });

  @observable.ref
  maxCreditAmountValue: number | undefined = undefined;

  private maxCreditAmountValidator = (
    $: string | number | NumberModel | undefined,
  ) => {
    if (this.maxCreditAmountValue === undefined) return null;
    return lte(this.maxCreditAmountValue)($);
  };

  private globalAccountRequiredIfTransferToCreditAccount = ($: string) => {
    if (this.transferToCreditAccount) return required($);
    return null;
  };

  @observable.ref
  budgetItemOptions: IOption[] = [];

  readonly transferLocationOptions: IOption[] = [
    {
      value: "credit-accounts",
      label: t`Transfer to credit account`,
    },
    { value: "projects", label: t`Transfer to project` },
  ];

  creditForm = new FormState({
    global_account_id: new FieldState("").validators(
      this.globalAccountRequiredIfTransferToCreditAccount,
    ),
    to_account_id: new FieldState("").validators(required),
    amount: new FieldState("").validators(
      required,
      gt(0),
      this.maxCreditAmountValidator,
    ),
    date: new FieldState<Date | undefined>(new Date()).validators(required),
    transfer_location: new FieldState<"credit-accounts" | "projects">(
      "credit-accounts",
    ),
    project_id: new FieldState(""),
  });

  private transferGlobalCreditMutation = new MutationHelper<
    TransferGlobalCreditMutation,
    TransferGlobalCreditMutationVariables
  >(TransferGlobalCreditDocument);

  private projectBudgetItemsFetcher = new FetchHelper<
    GetProjectBudgetItemsQuery,
    GetProjectBudgetItemsQueryVariables
  >(GetProjectBudgetItemsDocument);

  constructor(
    private appStore: AppStore,
    private onAction:
      | ((data: TOnCompletePayload | TOnReopenPayload) => void)
      | undefined,
    private setMaxAmountManually = false,
  ) {
    makeObservable(this);
  }

  @computed
  get transferToCreditAccount() {
    return this.creditForm.$.transfer_location.value === "credit-accounts";
  }

  @action.bound
  handleAction(
    data: TOnCompletePayload | TOnReopenPayload,
    expenseItem: ExpenseItem,
  ) {
    expenseItem?.setCompletedAt(data.completed_at);
    expenseItem?.updateStats(data.stats);
    this.onAction?.(data);
  }

  @action.bound
  setMaxCreditAmount(amount: number | undefined) {
    this.maxCreditAmountValue = amount;
  }

  @action.bound
  handleSelectCreditAccount(
    accountId: string,
    creditAccount: GlobalCreditAccountFragment | undefined,
  ) {
    if (!creditAccount) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Selected global credit account is missing it's credit account link.`,
      });
      captureEvent({
        message: "FE: Global credit account is missing it's credit account.",
      });
      return;
    }
    this.creditForm.$.global_account_id.onChange(accountId);
    this.creditForm.$.to_account_id.onChange(creditAccount.creditAccount.id);
    if (this.setMaxAmountManually) return;
    this.setMaxCreditAmount(
      nominate(
        creditAccount.stats.remaining_credit,
        this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ),
    );
  }

  @computed
  get isCreditTransferLoading(): boolean {
    return this.transferGlobalCreditMutation.isLoading.value;
  }

  @computed
  get isBudgetItemOptionsLoading(): boolean {
    return this.projectBudgetItemsFetcher.isLoading.value;
  }

  @action.bound
  async handleSelectProject(projectId: string): Promise<void> {
    this.creditForm.$.project_id.onChange(projectId);

    const [data, error] = await this.projectBudgetItemsFetcher.fetch({
      id: projectId,
    });

    if (error || !data.project) return;

    this.budgetItemOptions = data.project.expenses.map((expense) => ({
      label: `${expense.title} (${currency.formatByWorkspace(
        expense.stats.credit,
      )}) - ${t`uninvoiced:`} ${currency.formatByWorkspace(expense.stats.uninvoiced)}`,
      value: expense.creditAccount!.id,
    }));
  }

  @action.bound
  async handleTransferCredit(
    sourceIds: GlobalCreditAccountTransferSourceIds,
    onSuccess: () => void,
  ) {
    if (!sourceIds.toId && !sourceIds.fromId) {
      this.appStore.UIStore.toast({
        status: "error",
        title: t`Source credit account is missing.`,
      });
      captureEvent({
        message: "FE: Source credit account is missing.",
      });
      return;
    }

    const result = await this.creditForm.validate();
    if (result.hasError) return;

    const [, error] = await this.transferGlobalCreditMutation.mutate({
      amount: denominate(
        Number(result.value.amount.$),
        this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ),
      fromCreditAccountId: sourceIds.fromId ?? result.value.to_account_id.$,
      toCreditAccountId: sourceIds.toId ?? result.value.to_account_id.$,
      performedAt: toApiDateTime(result.value.date.$ ?? new Date()),
    });

    if (error) return;

    this.appStore.UIStore.toast({
      status: "success",
      title: t`Credit transferred successfully`,
    });
    this.getCreditModalDisclosure.onClose();
    onSuccess();
  }
}
